/**
 * Formatters specific to Search Visibility.
 *
 * Visibility, share of voice and average position all move in **percentage
 * points**, not percent changes — Semrush reports "+8.19" when visibility goes
 * from 16.35% to 24.53%. Reusing the GTM `fmtDelta` here would print "+8.2%"
 * and read as a relative change, which is a different (and wrong) number.
 */

export function fmtVisibility(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(2)}%`;
}

/** Semrush's traffic estimate, as whole visits — "0.25 visits" means nothing to a reader. */
export function fmtTraffic(value: number | null): string {
  return value === null ? '—' : Math.round(value).toLocaleString('en-GB');
}

export function fmtPosition(value: number | null): string {
  // 100 is Semrush's "ranks for nothing" sentinel, not a real 100th place.
  if (value === null) return '—';
  return value >= 100 ? '—' : value.toFixed(1);
}

/** A percentage-point change: "+8.19 pts". */
export function fmtPoints(value: number | null): { text: string; trend: 'up' | 'down' | 'neutral' } {
  if (value === null || Number.isNaN(value)) return { text: '—', trend: 'neutral' };
  const rounded = Number(value.toFixed(2));
  if (rounded === 0) return { text: '0 pts', trend: 'neutral' };
  return {
    // A real minus sign, as the relative change beside it ("−5.50 pts (−28%)").
    text: `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(2)} pts`,
    trend: rounded > 0 ? 'up' : 'down',
  };
}

/** A change in Semrush's traffic estimate, in whole visits: "+2". */
export function fmtTrafficDelta(value: number | null): { text: string; trend: 'up' | 'down' | 'neutral' } {
  if (value === null || Number.isNaN(value)) return { text: '—', trend: 'neutral' };
  const rounded = Math.round(value);
  if (rounded === 0) return { text: '0', trend: 'neutral' };
  return { text: `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toLocaleString('en-GB')}`, trend: rounded > 0 ? 'up' : 'down' };
}

/**
 * A position change. Semrush prints "↑8" for eight places gained, so a positive
 * number is an improvement even though the underlying position went down. The
 * text is the bare number: TrendPill draws the arrow.
 */
export function fmtPositionChange(value: number | null): { text: string; trend: 'up' | 'down' | 'neutral' } {
  if (value === null || value === 0) return { text: '—', trend: 'neutral' };
  return { text: `${Math.abs(value)}`, trend: value > 0 ? 'up' : 'down' };
}

/**
 * Average position improves as it falls, so a signed number next to a green
 * arrow ("↑ -12.8") reads backwards. Say it in words instead: "12.8 places
 * better", coloured by whether it is good news.
 */
export function fmtAvgPositionChange(value: number | null): { text: string; trend: 'up' | 'down' | 'neutral' } {
  if (value === null || Number.isNaN(value)) return { text: '—', trend: 'neutral' };
  const rounded = Number(value.toFixed(1));
  if (rounded === 0) return { text: 'No change', trend: 'neutral' };
  const places = Math.abs(rounded).toFixed(1);
  return rounded < 0
    ? { text: `${places} places better`, trend: 'up' }
    : { text: `${places} places worse`, trend: 'down' };
}

/** Fixed names, as in utils/dateFormat: en-GB prints "Sept" in recent browsers. */
const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const;

/**
 * A week label: "1–8 Sep", or "29 Sep – 6 Oct" when the week spans two months.
 * `weekEnd` is the week's last harvest day, so it is often not the Sunday.
 */
export function fmtWeek(weekStart: string, weekEnd: string): string {
  const start = new Date(`${weekStart}T00:00:00Z`);
  const end = new Date(`${weekEnd}T00:00:00Z`);
  const day = (d: Date) => d.getUTCDate();
  const month = (d: Date) => MONTHS_SHORT[d.getUTCMonth()];
  return start.getUTCMonth() === end.getUTCMonth()
    ? `${day(start)}–${day(end)} ${month(end)}`
    : `${day(start)} ${month(start)} – ${day(end)} ${month(end)}`;
}

/** "sequesto.com" → "sequesto.com"; a full URL → its path, for landing-page tables. */
export function fmtPath(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.pathname === '/' ? parsed.hostname : parsed.pathname;
  } catch {
    return url;
  }
}

/** Days of slack past the refresh interval before a harvest counts as stale. */
const STALE_MARGIN_DAYS = 2;
/** Used when the payload predates `sync`: the weekly throttle in tracking-sync.ts. */
const DEFAULT_REFRESH_DAYS = 6;

export interface HarvestFreshness {
  label: string;
  /** ok: within the weekly cadence. stale: older than that. failing: the last sync run failed. none: no harvest. */
  tone: 'ok' | 'stale' | 'failing' | 'none';
  /** Stale or failing: the chip links to /integrations, where the sync can be checked and re-run. */
  linkToIntegrations: boolean;
  /** Hover text: cadence, last pull, and the sync error if any. */
  title: string;
}

/**
 * The Search Visibility freshness chip. The Position Tracking pull runs about
 * weekly to save Semrush API units, so on 12 Oct a 7 Oct harvest (5 days) was
 * normal, yet the chip went amber after 3 days as if the sync had stopped.
 */
export function harvestFreshness(
  lastCapturedDate: string | null,
  sync: { refreshEveryDays: number; lastPulledAt: string | null; lastRunStatus: string | null; lastRunError: string | null } | null | undefined,
  now: Date = new Date(),
): HarvestFreshness {
  const every = sync?.refreshEveryDays ?? DEFAULT_REFRESH_DAYS;
  const cadence = `Semrush Position Tracking is pulled about every ${every} days, to save API units.`;
  const pulled = sync?.lastPulledAt ? ` Last pull ${formatDayMonthUtc(sync.lastPulledAt)}.` : '';
  const failing = sync?.lastRunStatus === 'failed';
  const errorNote = failing ? ` The last Semrush sync failed${sync?.lastRunError ? `: ${sync.lastRunError}` : ''}.` : '';
  const title = `${cadence}${pulled}${errorNote}`;
  if (!lastCapturedDate) {
    return { label: 'No harvest for this market', tone: 'none', linkToIntegrations: failing, title };
  }
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const age = Math.round((today - Date.parse(`${lastCapturedDate}T00:00:00Z`)) / 86_400_000);
  const base = `Latest harvest ${formatDayMonthUtc(lastCapturedDate)}`;
  if (failing) return { label: `${base} · Semrush sync failing`, tone: 'failing', linkToIntegrations: true, title };
  if (age > every + STALE_MARGIN_DAYS) {
    return { label: `${base} · ${age} days old`, tone: 'stale', linkToIntegrations: true, title };
  }
  return { label: `${base} · refreshed weekly`, tone: 'ok', linkToIntegrations: false, title };
}

function formatDayMonthUtc(iso: string): string {
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  return `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
}

/**
 * Landing-page traffic, with one decimal under 10: Semrush spreads a market's
 * estimate thinly over pages (25 pages from 0.1 to 1.1 visits on 7 Oct), and
 * whole visits rounded 24 of them to 0 and hid them.
 */
export function fmtTrafficFine(value: number | null): string {
  if (value === null) return '—';
  if (value === 0) return '0';
  if (Math.abs(value) >= 10) return fmtTraffic(value);
  const one = Number(value.toFixed(1));
  return one === 0 && value > 0 ? '<0.1' : one.toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** A landing-page traffic change at the same precision: "+0.7". */
export function fmtTrafficFineDelta(value: number | null): { text: string; trend: 'up' | 'down' | 'neutral' } {
  if (value === null || Number.isNaN(value)) return { text: '—', trend: 'neutral' };
  if (Math.abs(value) >= 10) return fmtTrafficDelta(value);
  const one = Number(value.toFixed(1));
  if (one === 0) return { text: '0', trend: 'neutral' };
  return { text: `${one > 0 ? '+' : '−'}${Math.abs(one).toFixed(1)}`, trend: one > 0 ? 'up' : 'down' };
}

/** A relative fall at or beyond this over the last four weeks raises the alert. */
export const SHARP_DROP = 0.3;

export interface RecentVisibilityChange {
  /** Newest full week and the week four weeks before it. */
  from: { weekStart: string; weekEnd: string; visibility: number; top10: number | null };
  to: { weekStart: string; weekEnd: string; visibility: number; top10: number | null; harvestDays?: number };
  points: number;
  /** Relative change, e.g. -0.55 for a 55% fall; null when the base is 0. */
  relative: number | null;
  sharpDrop: boolean;
}

/**
 * Pure: SERP visibility in the newest *full* week against the week four weeks
 * before it, from the week-by-week rows (newest first). A partial newest week
 * is skipped: on 12 Oct the headline set 20.87% (7–13 Sep) against 9.39% for a
 * 3-day week (5–7 Oct) and read "−55%" while the full weeks said −28%. Rows
 * without `harvestDays` (older payloads) count as full. Null with too little
 * history.
 */
export function recentVisibilityChange(
  weeks: ReadonlyArray<{ weekStart: string; weekEnd: string; visibility: number | null; top10: number | null; harvestDays?: number }>,
  span = 4,
): RecentVisibilityChange | null {
  const newest = weeks.findIndex((w) => w.harvestDays === undefined || w.harvestDays >= 7);
  if (newest < 0) return null;
  const to = weeks[newest];
  const from = weeks[newest + span];
  if (!to || !from || to.visibility === null || from.visibility === null) return null;
  const points = to.visibility - from.visibility;
  const relative = from.visibility > 0 ? points / from.visibility : null;
  return {
    from: { weekStart: from.weekStart, weekEnd: from.weekEnd, visibility: from.visibility, top10: from.top10 },
    to: { weekStart: to.weekStart, weekEnd: to.weekEnd, visibility: to.visibility, top10: to.top10, harvestDays: to.harvestDays },
    points,
    relative,
    sharpDrop: relative !== null && relative <= -SHARP_DROP,
  };
}

function shiftIso(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The KPI tiles' change window in dates, so it can be read against the weekly
 * headline: "1–7 Oct vs 14–20 Jul". The API averages the period's last and
 * first `days` calendar days (see averagingDays in search-visibility.ts).
 */
export function fmtChangeWindow(firstCaptured: string | null | undefined, lastCaptured: string | null | undefined, days: number | null | undefined): string | null {
  if (!firstCaptured || !lastCaptured) return null;
  const n = Math.max(1, days ?? 1);
  const span = (a: string, b: string) => (a === b ? formatDayMonthUtc(a) : fmtWeek(a, b));
  return `${span(shiftIso(lastCaptured, -(n - 1)), lastCaptured)} vs ${span(firstCaptured, shiftIso(firstCaptured, n - 1))}`;
}

/**
 * Pure: a visibility change too small to call. On 13 Oct the 90-day tile read a
 * green "+0.12 pts" (11.35% → 11.48%, 1%) right under a headline saying −28%:
 * it looked like good news against a fall. Below a quarter of a point, or 5%
 * of the level, the change is shown without the good/bad colour.
 */
export function isFlatVisibilityChange(change: number | null, level: number | null): boolean {
  if (change === null || !Number.isFinite(change)) return true;
  const base = level !== null && Number.isFinite(level) ? Math.abs(level - change) : 0;
  return Math.abs(change) < Math.max(0.25, base * 0.05);
}

/** Pure: the best full week in the history (newest first), for the headline's arc. */
export function peakWeek<W extends { visibility: number | null; harvestDays?: number }>(weeks: ReadonlyArray<W>): W | null {
  let best: W | null = null;
  for (const w of weeks) {
    if (w.visibility === null || (w.harvestDays !== undefined && w.harvestDays < 7)) continue;
    if (!best || w.visibility > (best.visibility ?? -Infinity)) best = w;
  }
  return best;
}
